<template>
  <v-dialog
    :model-value="modelValue"
    width="500"
    @update:model-value="onDialogVisibility"
    @click:outside="closeModal"
  >
    <v-card rounded="lg">
      <div class="bg-red-600 relative">
        <i
          class="mdi mdi-close text-xl cursor-pointer text-white absolute top-2 right-3"
          @click="closeModal"
        ></i>
        <h2 class="text-xl font-semibold text-white py-5 pl-10">{{ title }}</h2>
      </div>
      <v-form ref="form" class="px-10 py-5">
        <div class="grid grid-cols-2 gap-3">
          <div class="col-span-2">
            <FormInput
              v-if="lockedKontraktor"
              label="Kontraktor"
              :model-value="lockedKontraktor.namaKontraktor"
              readonly
              disabled
            />
            <FormOption
              v-else
              label="Kontraktor"
              :loading="loadingKontraktor"
              :items="listKontraktor"
              item-value="id"
              item-title="namaKontraktor"
              :rules="[(v) => !!v || 'Kontraktor harus diisi']"
              return-object
              v-model="formDataLocal.kontraktor"
              required
              @input="onSearchKontraktor"
            />
          </div>
          <div class="col-span-2">
            <FormInput
              label="Judul Proyek"
              v-model="formDataLocal.judulProyek"
              required
              :rules="[(v) => !!v?.trim() || 'Judul Proyek harus diisi']"
            />
          </div>
          <div>
            <FormOption
              label="Lokasi Kerja"
              :loading="isLoadingLokasi"
              :items="listLokasi"
              item-value="id"
              item-title="location_name"
              return-object
              v-model="formDataLocal.lokasiKerja"
              required
              @input="onSearchLokasi"
              :rules="[(v) => !!v || 'Lokasi Kerja harus diisi']"
            />
          </div>
          <div>
            <FormOption
              label="Sub Lokasi Kerja"
              :loading="isLoadingSubLokasi"
              :items="listSubLokasi"
              item-value="id"
              item-title="name"
              return-object
              v-model="formDataLocal.subLokasi"
              multiple
              :disabled="!formDataLocal.lokasiKerja"
              required
              :chips="true"
              :closable-chips="true"
              :rules="[
                (v) =>
                  (Array.isArray(v) ? v.length > 0 : !!v) ||
                  'Sub Lokasi Kerja harus diisi',
              ]"
            />
          </div>
          <div class="col-span-2">
            <FormInput
              label="Keterangan Lokasi Kerja"
              v-model="formDataLocal.keteranganLokasiKerja"
              required
              :rules="[
                (v) => !!v?.trim() || 'Keterangan Lokasi Kerja harus diisi',
              ]"
            />
          </div>
          <div class="col-span-2">
            <FormButton
              label="Simpan"
              classes="w-full bg-red-600 text-white capitalize"
              @click="submitForm"
            />
          </div>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import GlobalDataMixin from "@/GlobalDataMixin";
import FormOption from "@/components/FormOption.vue";
import FormInput from "@/components/FormInput.vue";
import FormButton from "@/components/FormButton.vue";
import { API_BASE_URL, CMS_GET_IMKP_ACTIVE } from "@/constants";
import axios from "axios";
import { cloneDeep, debounce } from "lodash";
import { getESBLocation, getESBSubLocation } from "@/utilities/ESBE";
import { normalizeSubLokasiToNames } from "@/pages/imkp/core/subLokasiHelpers";

const url_list_kontrktor = API_BASE_URL + CMS_GET_IMKP_ACTIVE;

const FormCreatePengajuanIMKP = {
  kontraktor: null,
  judulProyek: null,
  lokasiKerja: null,
  subLokasi: [],
  keteranganLokasiKerja: null,
};

export default {
  name: "CreateImkpModal",
  mixins: [GlobalDataMixin],
  components: {
    FormOption,
    FormInput,
    FormButton,
  },
  emits: ["update:modelValue", "submit"],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Tambah IMKP",
    },
    lockedKontraktor: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formDataLocal: cloneDeep(FormCreatePengajuanIMKP),
      listKontraktor: [],
      listLokasi: [],
      listSubLokasi: [],
      loadingKontraktor: false,
      isLoadingLokasi: false,
      isLoadingSubLokasi: false,
    };
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.resetForm();
        if (!this.lockedKontraktor) {
          this.getListKontraktor();
        }
        this.getLocation(null);
      } else {
        this.resetForm();
      }
    },
    "formDataLocal.lokasiKerja"(newVal, oldVal) {
      if (newVal?.id === oldVal?.id) return;
      this.formDataLocal.subLokasi = [];
      if (newVal?.id) {
        this.getSubLocation(null);
      } else {
        this.listSubLokasi = [];
      }
    },
  },
  methods: {
    resetForm() {
      this.formDataLocal = cloneDeep(FormCreatePengajuanIMKP);
      if (this.lockedKontraktor) {
        this.formDataLocal.kontraktor = cloneDeep(this.lockedKontraktor);
      }
      this.listSubLokasi = [];
    },
    onSearchKontraktor(event) {
      const keyword =
        typeof event === "string" ? event : event?.target?.value ?? null;
      this.getListKontraktor(keyword);
    },
    onSearchLokasi(event) {
      const keyword =
        typeof event === "string" ? event : event?.target?.value ?? null;
      if (keyword != null || event == null) {
        this.getLocation(keyword);
      }
    },
    getListKontraktor: debounce(function (keyword = null) {
      if (this.formDataLocal.kontraktor && !keyword) {
        return;
      }

      this.loadingKontraktor = true;

      const params = {
        search: keyword,
        page: 1,
        limit: 10,
        sort: "namaKontraktor",
        order: "asc",
      };

      axios
        .get(url_list_kontrktor, {
          params,
          headers: {
            role: "isafe",
            Authorization: "Bearer " + this.userData.token,
            accept: "application/json",
          },
        })
        .then((res) => {
          this.listKontraktor = res.data.data;
        })
        .catch(() => {
          this.listKontraktor = [];
        })
        .finally(() => {
          this.loadingKontraktor = false;
        });
    }, 1000),
    async getLocation(value) {
      try {
        this.isLoadingLokasi = true;
        const res = await getESBLocation(value);
        this.listLokasi = res.dataLocation;
      } catch {
        this.listLokasi = [];
      } finally {
        this.isLoadingLokasi = false;
      }
    },
    async getSubLocation(event = null) {
      const inputValue =
        typeof event === "string" ? event : event?.target?.value ?? null;
      const id_location = this.formDataLocal.lokasiKerja?.id;
      if (!id_location) {
        this.listSubLokasi = [];
        return;
      }

      try {
        this.isLoadingSubLokasi = true;
        const res = await getESBSubLocation(id_location, inputValue);
        this.listSubLokasi = res.dataSubLocation;
      } catch {
        this.listSubLokasi = [];
      } finally {
        this.isLoadingSubLokasi = false;
      }
    },
    async submitForm() {
      const validate = await this.$refs.form.validate();
      if (!validate.valid) {
        return;
      }

      const payload = cloneDeep(this.formDataLocal);
      payload.subLokasi = normalizeSubLokasiToNames(payload.subLokasi);
      this.$emit("submit", payload);
    },
    onDialogVisibility(open) {
      this.$emit("update:modelValue", open);
    },
    closeModal() {
      this.onDialogVisibility(false);
    },
  },
};
</script>
